import { useState, useRef, useEffect, useCallback } from "react";
import { DashboardRole } from "../../types.js";
import {
  Search,
  Info,
  ChevronDown,
  User,
  Settings,
  LogOut,
} from "lucide-react";
import type {
  DashboardGlobalSearchResult,
  UseDashboardGlobalSearchResult,
} from "../../hooks/useDashboardGlobalSearch";
import { DashboardGlobalSearch } from "../search/DashboardGlobalSearch";

/* ── Fluid clamp tokens ── */
const cl = {
  headerMinH: "clamp(60px, 6.94vh, 100px)",
  headerPx: "clamp(24px, 4.44vw, 64px)",
  leftGap: "clamp(24px, 2.43vw, 35px)",
  rightGap: "clamp(20px, 2.08vw, 30px)",
  infoIcon: "clamp(22px, 1.67vw, 24px)",
  searchW: "clamp(240px, 20vw, 320px)",
  searchH: "clamp(36px, 3.33vh, 40px)",
  searchIcon: "clamp(16px, 1.25vw, 18px)",
  searchRadius: "clamp(8px, 0.83vw, 12px)",
  searchPad: "clamp(12px, 1.11vw, 16px)",
  fontSize: "clamp(13px, 0.97vw, 14px)",
  fontSizeSm: "clamp(11px, 0.83vw, 12px)",
  badgeSize: "clamp(16px, 1.25vw, 18px)",
  badgeFont: "clamp(11px, 0.65vw, 13px)",
  bellW: "clamp(24px, 1.67vw, 24px)",
  bellH: "clamp(24px, 1.67vw, 28px)",
  flagW: "clamp(36px, 2.78vw, 40px)",
  flagH: "clamp(24px, 1.88vh, 27px)",
  langGap: "clamp(8px, 0.83vw, 12px)",
  avatarSize: "clamp(36px, 2.5vw, 40px)",
  profileGap: "clamp(10px, 1.11vw, 16px)",
  roleMargin: "clamp(2px, 0.28vh, 4px)",
  langDropW: "clamp(140px, 13.89vw, 200px)",
  userDropW: "clamp(180px, 16.67vw, 240px)",
} as const;

/* ── Language options ── */
const LANGUAGES = [
  { code: "en", label: "English", flag: "https://flagcdn.com/w40/us.png" },
  { code: "es", label: "Español", flag: "https://flagcdn.com/w40/es.png" },
  { code: "zh", label: "中文", flag: "https://flagcdn.com/w40/cn.png" },
  { code: "ja", label: "日本語", flag: "https://flagcdn.com/w40/jp.png" },
] as const;

/* ── Bell SVG ── */
function BellIcon({ width, height }: { width: string; height: string }) {
  return (
    <svg
      viewBox="0 0 24 28"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width, height }}
    >
      <defs>
        <linearGradient
          id="bellGradient"
          gradientUnits="userSpaceOnUse"
          x1="11.75"
          y1="0"
          x2="11.75"
          y2="19.4756"
        >
          <stop stopColor="#FD936E" />
          <stop offset="0.65" stopColor="#8181FF" />
        </linearGradient>
      </defs>
      <path
        d="M12 0C11.2583 0 10.6667 0.591667 10.6667 1.33333V2.05C7.70833 2.575 5.5 5.08333 5.5 8.16667V13.6667L3.16667 16V17.3333H20.8333V16L18.5 13.6667V8.16667C18.5 5.08333 16.2917 2.575 13.3333 2.05V1.33333C13.3333 0.591667 12.7417 0 12 0ZM9.33333 18.6667C9.33333 20.1417 10.525 21.3333 12 21.3333C13.475 21.3333 14.6667 20.1417 14.6667 18.6667H9.33333Z"
        fill="url(#bellGradient)"
      />
    </svg>
  );
}

/* ── Dropdown backdrop ── */
function DropdownBackdrop({ onClick }: { onClick: () => void }) {
  return (
    <div
      className="fixed inset-0 z-10"
      aria-hidden="true"
      onClick={onClick}
    />
  );
}

interface DashboardHeaderProps {
  badge: string;
  displayName: string;
  onOpenSettings: () => void;
  role: DashboardRole;
  onOpenNavigation: () => void;
  onSelectSearchResult: (result: DashboardGlobalSearchResult) => void;
  globalSearch: Pick<
    UseDashboardGlobalSearchResult,
    "errorMessage" | "groups" | "isLoading" | "query" | "setQuery"
  >;
  onSignOut: () => void;
  notificationCount?: number;
  avatarUrl?: string;
}

export function DashboardHeader({
  badge,
  displayName,
  onOpenSettings,
  role,
  onOpenNavigation,
  onSelectSearchResult,
  globalSearch,
  onSignOut,
  notificationCount = 3,
  avatarUrl,
}: DashboardHeaderProps) {
  const [langOpen, setLangOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState<(typeof LANGUAGES)[number]>(LANGUAGES[0]);

  const dmSans = "'DM Sans', sans-serif";
  const dmRegular = "'opsz' 14";
  const dmMedium = "'opsz' 14, 'wght' 500";
  const dmSemiBold = "'opsz' 14, 'wght' 600";

  const badgeDisplay = notificationCount > 9 ? "9+" : `${notificationCount}`;

  return (
    <div
      className="topbar-wrapper"
      style={{
        width: "100%",
        background: "var(--topbar-bg, #FFFFFF)",
        borderBottom: "1px solid var(--topbar-border, #e0e0e0)",
        paddingTop: "16px",
        paddingBottom: "16px",
        marginBottom: "0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          minHeight: cl.headerMinH,
          paddingLeft: cl.headerPx,
          paddingRight: cl.headerPx,
          fontFamily: dmSans,
          fontVariationSettings: dmMedium,
        }}
      >
        {/* ── Left section ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            gap: cl.leftGap,
          }}
        >
          {/* Info button */}
          <button
            aria-label="More information"
            type="button"
            className="topbar-hover-btn hidden lg:flex"
            style={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              border: "none",
              background: "transparent",
              transition: "opacity 200ms",
            }}
          >
            <Info
              strokeWidth={2}
              style={{
                width: cl.infoIcon,
                height: cl.infoIcon,
                color: "var(--topbar-muted, #b3b3b3)",
              }}
            />
          </button>

          {/* Search bar */}
          <form
            className="hidden lg:flex"
            onSubmit={(e) => e.preventDefault()}
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              width: cl.searchW,
              height: cl.searchH,
            }}
          >
            <span
              style={{
                position: "absolute",
                left: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
                paddingLeft: cl.searchPad,
              }}
            >
              <Search
                strokeWidth={2}
                style={{
                  width: cl.searchIcon,
                  height: cl.searchIcon,
                  color: "var(--topbar-muted, #b3b3b3)",
                }}
              />
            </span>
            <input
              aria-label="Search"
              type="search"
              placeholder="Search..."
              value={globalSearch.query}
              onChange={(e) => globalSearch.setQuery(e.target.value)}
              className="topbar-search-input"
              style={{
                width: "100%",
                height: "100%",
                background: "var(--topbar-bg, #FFFFFF)",
                border: "1px solid var(--topbar-search-border, #b3b3b3)",
                borderRadius: cl.searchRadius,
                outline: "none",
                paddingLeft: `calc(${cl.searchPad} * 2 + 18px)`,
                paddingRight: cl.searchPad,
                fontFamily: dmSans,
                fontSize: cl.fontSize,
                fontVariationSettings: dmRegular,
                color: "var(--topbar-text, #202224)",
                transition: "border-color 200ms",
              }}
            />
          </form>

          {/* Existing global search for dropdown results (hidden visually, uses the input above) */}
        </div>

        {/* ── Right section ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: cl.rightGap,
          }}
        >
          {/* Notification bell */}
          <button
            aria-label={`Notifications (${notificationCount} unread)`}
            type="button"
            className="topbar-hover-btn"
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: cl.bellW,
              height: cl.bellH,
              cursor: "pointer",
              border: "none",
              background: "transparent",
              transition: "opacity 200ms",
            }}
          >
            <BellIcon width={cl.bellW} height={cl.bellH} />
            {notificationCount > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: "-2px",
                  right: "-2px",
                  borderRadius: "50%",
                  background: "linear-gradient(to bottom, #FD936E, #8181FF)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: cl.badgeSize,
                  height: cl.badgeSize,
                  minWidth: "16px",
                  minHeight: "16px",
                  fontFamily: dmSans,
                  fontSize: cl.badgeFont,
                  fontWeight: 600,
                  lineHeight: "1.15",
                  color: "#FFFFFF",
                }}
              >
                {badgeDisplay}
              </span>
            )}
          </button>

          {/* Language selector */}
          <div style={{ position: "relative" }}>
            <button
              aria-label="Select language"
              aria-expanded={langOpen}
              aria-haspopup="true"
              type="button"
              className="topbar-hover-btn"
              onClick={() => setLangOpen((p) => !p)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: cl.langGap,
                cursor: "pointer",
                border: "none",
                background: "transparent",
                transition: "opacity 200ms",
              }}
            >
              <img
                src={selectedLang.flag}
                alt={selectedLang.label}
                style={{
                  width: cl.flagW,
                  height: cl.flagH,
                  flexShrink: 0,
                  overflow: "hidden",
                  borderRadius: "2px",
                  objectFit: "cover",
                }}
              />
              <span
                style={{
                  fontFamily: dmSans,
                  fontSize: cl.fontSize,
                  fontVariationSettings: dmSemiBold,
                  color: "var(--topbar-text, #202224)",
                  lineHeight: "normal",
                }}
              >
                {selectedLang.label}
              </span>
              <ChevronDown
                size={8}
                strokeWidth={2}
                style={{
                  color: "var(--topbar-icon, #000000)",
                  transition: "transform 200ms",
                  transform: langOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </button>

            {langOpen && (
              <>
                <DropdownBackdrop onClick={() => setLangOpen(false)} />
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    marginTop: "8px",
                    background: "var(--topbar-dropdown-bg, #FFFFFF)",
                    border: "1px solid var(--topbar-dropdown-border, #e0e0e0)",
                    borderRadius: "8px",
                    boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)",
                    overflow: "hidden",
                    zIndex: 20,
                    minWidth: cl.langDropW,
                  }}
                >
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => {
                        setSelectedLang(lang);
                        setLangOpen(false);
                      }}
                      className="topbar-dropdown-item"
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "8px 16px",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        transition: "background-color 200ms",
                        fontFamily: dmSans,
                        fontSize: cl.fontSize,
                        fontVariationSettings: dmMedium,
                        color: "var(--topbar-text, #202224)",
                        textAlign: "left",
                      }}
                    >
                      <img
                        src={lang.flag}
                        alt={lang.label}
                        style={{
                          width: "32px",
                          height: "20px",
                          borderRadius: "2px",
                          objectFit: "cover",
                        }}
                      />
                      {lang.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* User profile */}
          <div style={{ position: "relative" }}>
            <button
              aria-label="User menu"
              aria-expanded={userOpen}
              aria-haspopup="true"
              type="button"
              className="topbar-hover-btn"
              onClick={() => setUserOpen((p) => !p)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: cl.profileGap,
                cursor: "pointer",
                border: "none",
                background: "transparent",
                transition: "opacity 200ms",
              }}
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={displayName}
                  style={{
                    width: cl.avatarSize,
                    height: cl.avatarSize,
                    flexShrink: 0,
                    borderRadius: "50%",
                    overflow: "hidden",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: cl.avatarSize,
                    height: cl.avatarSize,
                    flexShrink: 0,
                    borderRadius: "50%",
                    background: "#d8d8d8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <User size={18} strokeWidth={2} color="var(--topbar-text, #202224)" />
                </div>
              )}
              <div className="hidden sm:block" style={{ textAlign: "left" }}>
                <div
                  style={{
                    fontFamily: dmSans,
                    fontSize: cl.fontSize,
                    fontVariationSettings: dmSemiBold,
                    color: "var(--topbar-text, #202224)",
                    lineHeight: "1",
                  }}
                >
                  {displayName}
                </div>
                <div
                  style={{
                    fontFamily: dmSans,
                    fontSize: cl.fontSizeSm,
                    fontVariationSettings: dmRegular,
                    fontWeight: "normal",
                    color: "var(--topbar-role, #6B7280)",
                    lineHeight: "1",
                    marginTop: cl.roleMargin,
                    textTransform: "capitalize",
                  }}
                >
                  {role}
                </div>
              </div>
              <ChevronDown
                size={8}
                strokeWidth={2}
                style={{
                  color: "var(--topbar-icon, #000000)",
                  transition: "transform 200ms",
                  transform: userOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </button>

            {userOpen && (
              <>
                <DropdownBackdrop onClick={() => setUserOpen(false)} />
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    marginTop: "8px",
                    background: "var(--topbar-dropdown-bg, #FFFFFF)",
                    border: "1px solid var(--topbar-dropdown-border, #e0e0e0)",
                    borderRadius: "8px",
                    boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)",
                    overflow: "hidden",
                    zIndex: 20,
                    minWidth: cl.userDropW,
                  }}
                >
                  {/* Dropdown header */}
                  <div
                    style={{
                      padding: "12px 16px",
                      borderBottom: "1px solid var(--topbar-dropdown-border, #e0e0e0)",
                    }}
                  >
                    <div
                      style={{
                        fontFamily: dmSans,
                        fontSize: cl.fontSize,
                        fontVariationSettings: dmSemiBold,
                        color: "var(--topbar-text, #202224)",
                      }}
                    >
                      {displayName}
                    </div>
                    <div
                      style={{
                        fontFamily: dmSans,
                        fontSize: cl.fontSizeSm,
                        fontVariationSettings: dmRegular,
                        color: "var(--topbar-role, #6B7280)",
                        marginTop: "2px",
                        textTransform: "capitalize",
                      }}
                    >
                      {role}
                    </div>
                  </div>

                  {/* View Profile */}
                  <button
                    type="button"
                    className="topbar-dropdown-item"
                    onClick={() => {
                      setUserOpen(false);
                    }}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "8px 16px",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      transition: "background-color 200ms",
                      fontFamily: dmSans,
                      fontSize: cl.fontSize,
                      fontVariationSettings: dmMedium,
                      color: "var(--topbar-text, #202224)",
                      textAlign: "left",
                    }}
                  >
                    <User size={16} strokeWidth={2} color="var(--topbar-text, #202224)" />
                    View Profile
                  </button>

                  {/* Settings */}
                  <button
                    type="button"
                    className="topbar-dropdown-item"
                    onClick={() => {
                      setUserOpen(false);
                      onOpenSettings();
                    }}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "8px 16px",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      transition: "background-color 200ms",
                      fontFamily: dmSans,
                      fontSize: cl.fontSize,
                      fontVariationSettings: dmMedium,
                      color: "var(--topbar-text, #202224)",
                      textAlign: "left",
                    }}
                  >
                    <Settings size={16} strokeWidth={2} color="var(--topbar-text, #202224)" />
                    Settings
                  </button>

                  {/* Divider */}
                  <div
                    style={{
                      height: "1px",
                      background: "var(--topbar-dropdown-border, #e0e0e0)",
                      margin: "4px 0",
                    }}
                  />

                  {/* Log Out */}
                  <button
                    type="button"
                    className="topbar-dropdown-item-logout"
                    onClick={() => {
                      setUserOpen(false);
                      onSignOut();
                    }}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "8px 16px",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      transition: "background-color 200ms",
                      fontFamily: dmSans,
                      fontSize: cl.fontSize,
                      fontVariationSettings: dmMedium,
                      color: "#ef4444",
                      textAlign: "left",
                    }}
                  >
                    <LogOut size={16} strokeWidth={2} color="#ef4444" />
                    Log Out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>
    </div>
  );
}
